import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCalendar, FiMapPin, FiUsers, FiGlobe, FiArrowLeft } from 'react-icons/fi';
import ProjectCard from '../components/ProjectCard';
import MediaGallery from '../components/MediaGallery';
import { getProjectById, getRelatedProjects } from '../utils/projectData';
import '../css/project-detail.css';

const ProjectDetail = () => {
  const { slug } = useParams();
  const project = getProjectById(slug);

  // Handle project not found
  if (!project) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-primary-800 mb-4">Project Not Found</h1>
          <p className="text-primary-600 mb-8">The project you're looking for doesn't exist.</p>
          <Link to="/projects" className="btn-primary">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  // Get related projects (other projects excluding current one)
  const relatedProjects = getRelatedProjects(project?.id, 3);

  return (
    <div className="min-h-screen pt-20">
      {/* Back Button */}
      <div className="container-custom py-6">
        <Link 
          to="/projects" 
          className="inline-flex items-center text-accent-600 hover:text-accent-700 font-medium"
        >
          <FiArrowLeft className="mr-2" />
          Back to Projects
        </Link>
      </div>

      {/* Hero Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="mb-6">
                <span className="inline-block px-3 py-1 text-sm font-semibold text-accent-600 bg-accent-50 rounded-full mb-4">
                  {project.category}
                </span>
                <h1 className="text-4xl md:text-5xl font-bold text-primary-800 mb-6">
                  {project.title}
                </h1>
                <p className="text-xl text-primary-600 leading-relaxed">
                  {project.description}
                </p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-4">
                  <FiCalendar className="text-accent-500" size={20} />
                  <span className="text-primary-700">{project.date}</span>
                </div>
                <div className="flex items-center space-x-4">
                  <FiMapPin className="text-accent-500" size={20} />
                  <span className="text-primary-700">{project.location}</span>
                </div>
                <div className="flex items-center space-x-4">
                  <FiUsers className="text-accent-500" size={20} />
                  <span className="text-primary-700">{project.credits}</span>
                </div>
                <div className="flex items-center space-x-4">
                  <FiGlobe className="text-accent-500" size={20} />
                  <span className="text-primary-700">{project.channels.join(', ')}</span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-primary-800 mb-4">Project Stats</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-2xl font-bold text-accent-600">{project.stats.viewers}</div>
                    <div className="text-sm text-primary-600">Live Viewers</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-accent-600">{project.stats.countries}</div>
                    <div className="text-sm text-primary-600">Countries Reached</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-accent-600">{project.stats.platforms}</div>
                    <div className="text-sm text-primary-600">Platforms</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-accent-600">{project.stats.duration}</div>
                    <div className="text-sm text-primary-600">Coverage Duration</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img
                src={project.image}
                alt={project.title}
                className="w-full rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <h2 className="text-3xl font-bold text-primary-800 mb-6">Project Overview</h2>
              <div className="prose prose-lg text-primary-600">
                <p className="mb-6">
                  {project.longDescription}
                </p>
                <p className="mb-6">
                  Our team worked closely with the festival organizers to ensure seamless integration with the event schedule. We deployed multiple camera positions strategically throughout the festival grounds, including aerial drone coverage that captured the breathtaking scale of the celebrations.
                </p>
                <p className="mb-6">
                  The live streaming component reached audiences across the Caribbean and beyond, with real-time engagement through social media platforms. Our post-production team worked around the clock to deliver same-day highlights and promotional content.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-primary-800 mb-4">Services Provided</h3>
                <ul className="space-y-3">
                  {project.services.map((service, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-accent-500 rounded-full flex-shrink-0"></div>
                      <span className="text-primary-700">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <MediaGallery media={project.gallery} title="Project Gallery" />

      {/* Related Projects */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-primary-800 mb-6">More Work</h2>
            <p className="text-xl text-primary-600 max-w-2xl mx-auto">
              Explore other projects that showcase our expertise and creativity
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {relatedProjects.map((relatedProject, index) => (
              <motion.div
                key={relatedProject.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <ProjectCard {...relatedProject} />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-12"
          >
            <Link to="/projects" className="btn-secondary">
              View All Projects
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
